import React from 'react';
import PropTypes from 'prop-types';
import clsx from 'clsx';
import { Box, Chip, Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';

const useStyles = makeStyles((theme) => ({
  featureSection: {
    marginBottom: theme.spacing(3),
    border: '1px solid #e0e0e0',
    borderRadius: theme.spacing(1),
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    padding: theme.spacing(1.25, 2),
    backgroundColor: '#f0f0f0',
  },
  coverageTitle: {
    fontWeight: 700,
    fontSize: '0.85rem',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    color: '#222',
  },
  dispositionChip: {
    color: '#007913',
    backgroundColor: '#D4F9DC',
    fontWeight: 600,
    height: 22,
    fontSize: '0.75rem',
  },
  closedWithoutPayment: {
    color: '#006791',
    backgroundColor: 'rgb(0, 155, 218, 0.17)',
  },
  paidDisposition: {
    color: '#007913',
    backgroundColor: 'rgb(0, 155, 218, 0.17)',
  },
  actionNeededDisposition: {
    color: '#FF004E',
    backgroundColor: '#FFD4E7',
    fontWeight: 600,
  },
  deniedDisposition: {
    color: '#FF004E',
    backgroundColor: 'rgb(0, 155, 218, 0.17)',
  },
  partSummary: {
    padding: theme.spacing(2),
  },
  summaryGrid: {
    justifyContent: 'center',
  },
  summaryGridItem: {
    textAlign: 'center',
  },
  leftAlignedSummaryGridItem: {
    textAlign: 'left',
  },
  colHeader: {
    fontWeight: 700,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(0.5),
    textAlign: 'center',
  },
  colValue: {
    fontSize: '0.85rem',
    color: '#333',
    textAlign: 'center',
  },
  pricingBlock: {
    marginTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    marginLeft: 'auto',
    marginRight: 'auto',
    width: '100%',
    maxWidth: theme.spacing(34),
  },
  standalonePricingBlock: {
    marginTop: theme.spacing(2),
  },
  pricingRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(0.6, 0),
    borderBottom: '1px dashed #c9dff2',
    '&:last-child': { borderBottom: 'none' },
  },
  pricingLabel: {
    fontWeight: 700,
    fontSize: '0.85rem',
    color: '#333',
  },
  pricingValue: {
    fontSize: '0.85rem',
    color: '#333',
  },
  plainPricingRow: {
    borderBottom: 'none',
    padding: theme.spacing(0.25, 0),
  },
  pricingTitle: {
    fontWeight: 700,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(1),
    color: '#222',
    textAlign: 'center',
  },
  leftAlignedText: {
    textAlign: 'left',
  },
  leftAlignedPricingBlock: {
    marginLeft: 0,
    marginRight: 0,
    maxWidth: 'none',
  },
}));

const getDispositionClassName = (classes, disposition) => {
  switch (disposition) {
    case 'Action Needed':
      return classes.actionNeededDisposition;
    case 'Paid':
      return classes.paidDisposition;
    case 'Denied':
      return classes.deniedDisposition;
    case 'Closed Without Payment':
      return classes.closedWithoutPayment;
    default:
      return classes.dispositionChip;
  }
};

function DispositionChip({ disposition, classes }) {
  if (!disposition) return null;

  return (
    <Chip
      className={clsx(classes.dispositionChip, getDispositionClassName(classes, disposition))}
      label={disposition}
      size="small"
    />
  );
}

DispositionChip.propTypes = {
  disposition: PropTypes.string,
  classes: PropTypes.object.isRequired,
};

DispositionChip.defaultProps = {
  disposition: '',
};

const detailRowPropType = PropTypes.exact({
  key: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
});

const summaryFieldPropType = PropTypes.exact({
  key: PropTypes.string,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node]),
  xs: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  sm: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  md: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
});

const getFallbackGridSize = (fieldCount) => {
  if (fieldCount <= 1) return 12;
  if (fieldCount === 2) return 6;
  if (fieldCount === 3) return 4;
  if (fieldCount === 4) return 3;
  return 2;
};

const getDisplayValue = (value) => {
  if (value === undefined || value === null || value === '') return '--';
  return value;
};

function DetailRows({ classes, rows, title, variant, standalone, alignLeft }) {
  if (!rows.length) return null;

  return (
    <Box
      className={clsx(
        classes.pricingBlock,
        standalone && classes.standalonePricingBlock,
        alignLeft && classes.leftAlignedPricingBlock
      )}
    >
      {title && (
        <Typography
          className={clsx(classes.pricingTitle, alignLeft && classes.leftAlignedText)}
        >
          {title}
        </Typography>
      )}
      {rows.map((item, index) => (
        <Box
          key={item.key || `${item.label}-${index}`}
          className={clsx(
            classes.pricingRow,
            variant === 'plain' && classes.plainPricingRow
          )}
        >
          <Typography className={clsx(alignLeft && classes.leftAlignedText, classes.pricingLabel)}>
            {item.label}
          </Typography>
          <Typography className={clsx(alignLeft && classes.leftAlignedText, classes.pricingValue)}>
            {item.value}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

DetailRows.propTypes = {
  classes: PropTypes.object.isRequired,
  rows: PropTypes.arrayOf(detailRowPropType),
  title: PropTypes.string,
  variant: PropTypes.oneOf(['dashed', 'plain']),
  standalone: PropTypes.bool,
  alignLeft: PropTypes.bool,
};

DetailRows.defaultProps = {
  rows: [],
  title: '',
  variant: 'dashed',
  standalone: false,
  alignLeft: false,
};

function CoverageSummary({
  coverageType,
  disposition,
  detailRows,
  detailTitle,
  detailRowsVariant,
  detailPlacement,
  detailAnchorField,
  summaryFields,
  variant,
}) {
  const classes = useStyles();
  const baseDetailRows = detailRows;
  const baseSummaryFields = summaryFields;
  const fallbackGridSize = getFallbackGridSize(baseSummaryFields.length);
  const resolvedSummaryFields = baseSummaryFields.map((field, index) => {
    return {
      ...field,
      key: field.key || `field-${index}`,
      value: getDisplayValue(field.value),
      xs: field.xs === undefined ? 12 : field.xs,
      sm: field.sm === undefined ? fallbackGridSize : field.sm,
      md: field.md,
    };
  });
  const resolvedDetailRows = baseDetailRows.map((row, index) => {
    return {
      ...row,
      key: row.key || `detail-${index}`,
      value: getDisplayValue(row.value),
    };
  });
  const resolvedVariant =
    variant === 'details-only' ? 'standalone-details' : variant;
  const showSummaryFields =
    variant !== 'details-only' && resolvedSummaryFields.length > 0;
  const showStandaloneDetails =
    resolvedDetailRows.length > 0 &&
    (resolvedVariant === 'standalone-details' || detailPlacement === 'standalone');
  const showInlineDetails =
    resolvedDetailRows.length > 0 && !showStandaloneDetails && showSummaryFields;
  const anchorFieldKey = detailAnchorField || resolvedSummaryFields[0]?.key;
  const isDefaultVariant = resolvedVariant === 'default';

  return (
    <Box className={classes.featureSection}>
      <Box className={classes.header}>
        <Typography className={classes.coverageTitle}>{coverageType || '--'}</Typography>
        <DispositionChip classes={classes} disposition={disposition} />
      </Box>

      <Box className={classes.partSummary}>
        {showSummaryFields && (
          <Grid container spacing={2} alignItems="flex-start" className={classes.summaryGrid}>
            {resolvedSummaryFields.map((field) => (
              <Grid
                item
                key={field.key}
                xs={field.xs}
                sm={field.sm}
                md={field.md}
                className={clsx(
                  classes.summaryGridItem,
                  isDefaultVariant && classes.leftAlignedSummaryGridItem
                )}
              >
                <Typography
                  className={clsx(classes.colHeader, isDefaultVariant && classes.leftAlignedText)}
                >
                  {field.label}
                </Typography>
                <Typography
                  className={clsx(classes.colValue, isDefaultVariant && classes.leftAlignedText)}
                >
                  {field.value}
                </Typography>
                {showInlineDetails && field.key === anchorFieldKey && (
                  <DetailRows
                    classes={classes}
                    rows={resolvedDetailRows}
                    title={detailTitle}
                    variant={detailRowsVariant}
                    alignLeft={isDefaultVariant}
                  />
                )}
              </Grid>
            ))}
          </Grid>
        )}
        {showStandaloneDetails && (
          <DetailRows
            classes={classes}
            rows={resolvedDetailRows}
            title={detailTitle}
            variant={detailRowsVariant}
            standalone
            alignLeft={isDefaultVariant}
          />
        )}
      </Box>
    </Box>
  );
}

CoverageSummary.propTypes = {
  coverageType: PropTypes.string,
  disposition: PropTypes.string,
  detailRows: PropTypes.arrayOf(detailRowPropType),
  detailTitle: PropTypes.string,
  detailRowsVariant: PropTypes.oneOf(['dashed', 'plain']),
  detailPlacement: PropTypes.oneOf(['inline', 'standalone']),
  detailAnchorField: PropTypes.string,
  summaryFields: PropTypes.arrayOf(summaryFieldPropType),
  variant: PropTypes.oneOf(['default', 'standalone-details', 'details-only']),
};

CoverageSummary.defaultProps = {
  coverageType: '',
  disposition: '',
  detailRows: [],
  detailTitle: '',
  detailRowsVariant: 'dashed',
  detailPlacement: 'inline',
  detailAnchorField: '',
  summaryFields: [],
  variant: 'default',
};

export default CoverageSummary;
